<template>
  <div class="digital-donation-receipt">
    <p-progress-linear v-if="isLoading" indeterminate></p-progress-linear>
    <p-form v-if="!isLoading && _.isEmpty(errorResponse) && !isSubmitted" :model="formPayload">
      <p-row>
        <p-col>
          <p-text-field
            v-model="formPayload.contact.eMail"
            type="email"
            name="email"
            autocomplete="email"
            :label="$t('core.address.email')"
            :required="!isEmailPrefilled"
            :disabled="isEmailPrefilled"
          ></p-text-field>
        </p-col>
      </p-row>
      <p-dynamic-form
        v-model="formPayload"
        :summary.sync="formSummary"
        :json-schema.sync="formPayload.schema"
        :form-definition="formDefinition"
        no-contact-fields
        no-address-fields
      ></p-dynamic-form>
    </p-form>
    <p-request-alert v-if="_.isSet(errorResponse)" v-model="errorResponse" class="mb-3 mt-3"></p-request-alert>
    <p-alert type="success" class="mb-3 mt-3" :value="isShowSubmitted">
      {{ $t('core.page.dynamicForm.message.dynamicFormWasSentSuccessfully') }}
    </p-alert>
    <p-row>
      <p-col v-if="!isSubmitted" justify-end>
        <p-button :disabled="!canPostForm" :loading="isSubmitting" @click="postForm">
          {{ $t('core.app.send') }}
        </p-button>
      </p-col>
      <p-col v-else justify-start>
        <p-button @click="windowBack">
          {{ $t('core.app.back') }}
        </p-button>
      </p-col>
    </p-row>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import { IJSONSchema } from '@glittr/frontend-core/src/plugins/validation/IJSONSchema';
  import DynamicFormDefinitionResourceViewModel from '@/src/services/v1/viewModel/resource/DynamicFormDefinitionResourceViewModel';
  import DynamicFormPayloadResourceViewModel from '@/src/services/v1/viewModel/resource/DynamicFormPayloadResourceViewModel';
  import DynamicFormContactResourceViewModel from '@/src/services/v1/viewModel/resource/DynamicFormContactResourceViewModel';
  import GetDigitalDonationReceiptFormDefinitionRequestViewModel from '@/src/services/v1/viewModel/request/DigitalDonationReceipt/GetDigitalDonationReceiptFormDefinitionRequestViewModel';
  import PostDigitalDonationReceiptFormRequestViewModel from '@/src/services/v1/viewModel/resource/PostDigitalDonationReceiptFormRequestViewModel';

  export interface Widget {
    onDataSubmitted?: (data: DynamicFormPayloadResourceViewModel) => void;
  }

  declare global {
    interface Window {
      glitWidget: Widget,
    }
  }

  export default Vue.extend({
    name: 'DigitalDonationReceipt',
    layout: 'layout-widget',
    props: {
      contactId: { default: undefined, type: String },
      definitionId: { default: undefined, type: String },
      businessUnit: { default: undefined, type: Number },
      appSystemKey: { default: undefined, type: String },
      addressOwner: { default: undefined, type: Number },
      submitRedirectUrl: { default: undefined, type: String },
      errorElementId: { default: undefined, type: String },
      successElementId: { default: undefined, type: String },
    },
    data: () => ({
      formSummary: {},
      isLoading: true,
      isSubmitting: false,
      errorResponse: undefined as any,
      formDefinition: new DynamicFormDefinitionResourceViewModel(),
      formPayload: new DynamicFormPayloadResourceViewModel().fromModel({
        fields: [],
        contact: new DynamicFormContactResourceViewModel(),
      }),
      isSubmitted: false,
      isShowSubmitted: false,
      isEmailPrefilled: false,
    }),
    computed: {
      canPostForm(): boolean {
        return !this.isSubmitting && this.formDefinition.initalized;
      },
      innerSubmitRedirectUrl(): string | undefined {
        if (this._.isSet(this.submitRedirectUrl)) {
          return this.submitRedirectUrl;
        }
        return this.$config.getQueryOrValue('widget-submit-redirect-url', 'submitredirecturl');
      },
      innerAppSystemKey(): string | undefined {
        if (this._.isSet(this.appSystemKey)) {
          return this.appSystemKey;
        }
        return this.$config.getQueryOrValue('widget-dynamicform-appsystemkey');
      },
      innerSuccessElementId(): string | undefined {
        if (this._.isSet(this.successElementId)) {
          return this.successElementId;
        }
        if (this.$config.values['widget-dynamicform-display-success-elementid']) {
          return this.$config.values['widget-dynamicform-display-success-elementid'];
        }
        return undefined;
      },
      innerErrorElementId(): string | undefined {
        if (this._.isSet(this.errorElementId)) {
          return this.errorElementId;
        }
        if (this.$config.values['widget-dynamicform-display-error-elementid']) {
          return this.$config.values['widget-dynamicform-display-error-elementid'];
        }
        return undefined;
      },
      innerContactId(): string | undefined {
        if (this._.isSet(this.contactId)) {
          return this.contactId;
        }
        return this.$config.getQueryOrValue('widget-dynamicform-contactid');
      },
      innerBusinessUnit(): number | undefined {
        if (this._.isSet(this.businessUnit)) {
          return this.businessUnit;
        }
        const businessUnit = this.$config.getQueryOrValue('widget-dynamicform-businessunit');
        return businessUnit ? +businessUnit : undefined;
      },
      innerAddressOwner(): number | undefined {
        if (this._.isSet(this.addressOwner)) {
          return this.addressOwner;
        }
        const addressOwner = this.$config.getQueryOrValue('widget-dynamicform-addressownernumber', 'addressowner');
        return addressOwner ? +addressOwner : undefined;
      },
      innerDefinitionId(): string | undefined {
        if (this._.isSet(this.definitionId)) {
          return this.definitionId;
        }
        return this.$config.getQueryOrValue('widget-dynamicform-formid', 'id');
      },
    },
    watch: {
      'formPayload.schema': function () {
        // The json schema is generated by the dynamic form, however as the email field is not visible in the dynamic form contact section,
        // the dynamic form does not mark it as required.
        // So that the validation works automatically when the user interacts with the email text field, the json schema is modified here
        // to mark the email as required.
        const required = (this.formPayload.schema?.properties?.contact as IJSONSchema | undefined)?.required;
        if (required && !required.includes('eMail')) {
          required.push('eMail');
        }
      },
    },
    beforeMount() {
      window.glitWidget ??= {};
    },
    async mounted() {
      await this.loadFormDefinition();
    },
    methods: {
      windowBack() {
        let backFunc = this.$router.back.bind(this.$router);
        if (this._.isSet(window?.history)) {
          backFunc = window.history.back.bind(window.history);
        } else if (typeof (window as any)?.back === 'function') {
          backFunc = (window as any).back.bind(window);
        }
        backFunc();
      },
      async postForm() {
        this.isSubmitting = true;
        try {
          if (this.innerErrorElementId !== undefined && this.innerErrorElementId !== '') {
            const el = document.getElementById(this.innerErrorElementId);
            if (el === null) {
              this.$log.warning(`Element with id '${this.innerErrorElementId}' not found.`);
            } else {
              el.classList.remove('glitWidgetElementVisible');
            }
          }
          if (!this.formPayload.validate()) {
            this.isSubmitting = false;
            return;
          }
          this.formPayload.definitionId = this.formDefinition?.id!;
          this.formPayload.languageIso2 = this.$translation.get() ?? this.$config.values['language-default'];
          this.formPayload.addressOwnerNumber = this.innerAddressOwner;
          this.formPayload.businessUnit = this.innerBusinessUnit;
          const req = new PostDigitalDonationReceiptFormRequestViewModel().fromModel({
            appSystemKey: this.innerAppSystemKey,
            form: this.formPayload,
          });
          await this.$service.v1.api.digitalDonationReceipt.postDigitalDonationReceiptForm(req);
          this.isSubmitted = true;
          this.emitSubmittedEvent();
          if (this.innerSuccessElementId !== undefined && this.innerSuccessElementId !== '') {
            const el = document.getElementById(this.innerSuccessElementId);
            if (el === null) {
              this.$log.warning(`Element with id '${this.innerSuccessElementId}' not found.`);
            } else {
              el.classList.add('glitWidgetElementVisible');
              return;
            }
          } else if (this.innerSubmitRedirectUrl !== undefined && this.innerSubmitRedirectUrl !== '') {
            window.location.href = this.innerSubmitRedirectUrl;
          }
          this.isShowSubmitted = true;
        } catch (error: any) {
          if (this.innerErrorElementId !== undefined && this.innerErrorElementId !== '') {
            const el = document.getElementById(this.innerErrorElementId);
            if (el === null) {
              this.$log.warning(`Element with id '${this.innerErrorElementId}' not found.`);
            } else {
              el.classList.add('glitWidgetElementVisible');
            }
          } else { this.errorResponse = error; }
        }
        this.isSubmitting = false;
      },
      async loadFormDefinition() {
        this.isLoading = true;
        try {
          const req = new GetDigitalDonationReceiptFormDefinitionRequestViewModel().fromModel({
            appSystemKey: this.innerAppSystemKey,
            id: this.innerDefinitionId,
            contactId: this.innerContactId,
          });
          this.errorResponse = undefined;
          const resp = await Vue.$service.v1.api.digitalDonationReceipt.getDigitalDonationReceiptFormDefinition(req);
          this.formDefinition = resp.definition;
          this.formPayload = resp.payload;

          this.isEmailPrefilled = !!this.formPayload.contact.eMail;
          this.formDefinition.caption = undefined; // Title of dynamic form should not be displayed
        } catch (error: any) {
          this.errorResponse = error;
        }
        this.isLoading = false;
      },
      emitSubmittedEvent() {
        if (window.glitWidget.onDataSubmitted) {
          window.glitWidget.onDataSubmitted(this.formPayload);
        }
      },
    },
  });
</script>
